import React from 'react';
import { TrendingUp, Activity } from 'lucide-react';

/**
 * PerformanceMetricsCard - Sport-agnostic performance data display
 *
 * Adapts labels and metrics based on detected sport:
 * - Baseball: Release velocity, plate velocity, spin rate, break
 * - Basketball: Shot velocity, rim velocity, arc
 * - Football: Release velocity, catch velocity, spiral, drop
 * - Soccer: Strike velocity, goal velocity, curve
 * - Hockey: Shot velocity, goalie velocity, elevation
 */

// Sport-specific label mappings
const SPORT_LABELS = {
  baseball: {
    initialVel: "Release Velocity",
    finalVel: "Plate Velocity",
    movement: "Break",
    spinLabel: "Spin Rate"
  },
  basketball: {
    initialVel: "Shot Velocity",
    finalVel: "Rim Velocity",
    movement: "Arc",
    spinLabel: "Rotation"
  },
  football: {
    initialVel: "Release Velocity",
    finalVel: "Catch Velocity",
    movement: "Drop/Drift",
    spinLabel: "Spiral Rate"
  },
  soccer: {
    initialVel: "Strike Velocity",
    finalVel: "Goal Velocity",
    movement: "Curve",
    spinLabel: "Spin"
  },
  hockey: {
    initialVel: "Shot Velocity",
    finalVel: "Goalie Velocity",
    movement: "Elevation",
    spinLabel: "Rotation"
  }
};

const PerformanceMetricsCard = ({ trajectoryData }) => {
  if (!trajectoryData) return null;

  const {
    sport = 'baseball',
    initial_velocity_mph,
    final_velocity_mph,
    spin_rate_rpm,
    movement
  } = trajectoryData;

  // Get sport-specific labels (fallback to baseball)
  const labels = SPORT_LABELS[sport?.toLowerCase()] || SPORT_LABELS.baseball;

  // Format movement display based on unit
  const formatMovement = (mv) => {
    if (!mv) return null;
    const vertText = `${Math.abs(mv.vertical).toFixed(1)}${mv.unit || 'in'} ${mv.vertical < 0 ? 'drop' : 'rise'}`;
    const horzText = `${Math.abs(mv.horizontal).toFixed(1)}${mv.unit || 'in'} horizontal`;
    return { vertText, horzText };
  };

  const movementDisplay = formatMovement(movement);

  return (
    <div className="bg-zinc-800/50 rounded-lg p-4 border border-cyan-500/20">
      {/* Header with sport badge */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-cyan-400">Performance Data</h3>
        {sport && (
          <span className="px-2 py-0.5 rounded text-xs bg-cyan-500/20 text-cyan-300 font-medium capitalize">
            {sport}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Initial Velocity */}
        {initial_velocity_mph !== undefined && initial_velocity_mph !== null && (
          <div>
            <p className="text-xs text-zinc-400 mb-1">{labels.initialVel}</p>
            <p className="text-2xl font-bold text-white">
              {initial_velocity_mph.toFixed(1)} <span className="text-lg text-zinc-400">mph</span>
            </p>
          </div>
        )}

        {/* Final Velocity */}
        {final_velocity_mph !== undefined && final_velocity_mph !== null && (
          <div>
            <p className="text-xs text-zinc-400 mb-1">{labels.finalVel}</p>
            <p className="text-xl font-semibold text-zinc-200">
              {final_velocity_mph.toFixed(1)} <span className="text-sm text-zinc-400">mph</span>
            </p>
          </div>
        )}

        {/* Spin Rate (if applicable) */}
        {spin_rate_rpm !== undefined && spin_rate_rpm !== null && (
          <div>
            <p className="text-xs text-zinc-400 mb-1 flex items-center gap-1">
              {labels.spinLabel}
              <Activity size={12} className="text-zinc-500" />
            </p>
            <p className="text-xl font-semibold text-cyan-400">
              {spin_rate_rpm.toFixed(0)} <span className="text-sm text-zinc-400">rpm</span>
            </p>
          </div>
        )}

        {/* Movement/Arc/Break */}
        {movementDisplay && (
          <div>
            <p className="text-xs text-zinc-400 mb-1 flex items-center gap-1">
              {labels.movement}
              <TrendingUp size={12} className="text-zinc-500" />
            </p>
            <p className="text-sm text-white leading-tight">
              {movementDisplay.vertText}
            </p>
            <p className="text-sm text-zinc-400 leading-tight">
              {movementDisplay.horzText}
            </p>
          </div>
        )}
      </div>

      {/* Additional sport-specific insights */}
      {trajectoryData.object_type && (
        <div className="mt-3 pt-3 border-t border-zinc-700">
          <p className="text-xs text-zinc-500">
            Tracking: <span className="text-zinc-400 capitalize">{trajectoryData.object_type}</span>
          </p>
        </div>
      )}
    </div>
  );
};

export default PerformanceMetricsCard;
